import { useState } from "react";
import { motion } from "framer-motion";
import { X, Lock, Loader2, Download, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND } from "@/lib/releases";

const Shell = ({ onClose, title, subtitle, children }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md" data-testid="release-modal">
    <motion.div initial={{ y: 30, scale: 0.95, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 24 }} onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0c0e]/95 p-7 shadow-[0_30px_100px_rgba(0,0,0,0.7)]">
      <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:bg-white/10 transition-colors" data-testid="release-modal-close">
        <X size={18} />
      </button>
      <div className="font-display text-2xl font-bold tracking-tight">{title}</div>
      <div className="text-white/50 text-xs mt-1 mb-6">{subtitle}</div>
      {children}
    </motion.div>
  </motion.div>
);

const Field = (props) => (
  <input {...props} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none transition-colors focus:border-white/50 placeholder:text-white/30" />
);

const SubmitBtn = ({ busy, icon: Icon, children, testid }) => (
  <button type="submit" disabled={busy} data-testid={testid}
          className="w-full mt-2 px-6 py-4 rounded-full bg-white text-black text-xs font-black tracking-[0.18em] inline-flex items-center justify-center gap-2 transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50">
    {busy ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />} {children}
  </button>
);

export const startDownload = (token) => window.location.assign(`${BACKEND}/api/releases/download/${token}`);

export const BuyModal = ({ release, onClose }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", delivery: "email" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      localStorage.setItem(`release_${release.slug}_email`, f.email.toLowerCase().trim());
      const r = await axios.post(`${BACKEND}/api/releases/${release.slug}/checkout`, { ...f, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed.");
      setBusy(false);
    }
  };

  return (
    <Shell onClose={onClose} title={`Buy ${release.title}`} subtitle={`$${release.price.toFixed(2)} · Song + cover art · Instant download`}>
      <form onSubmit={go} className="space-y-3" data-testid="buy-form">
        <Field placeholder="Full name" value={f.name} onChange={set("name")} required data-testid="buy-name" />
        <Field type="email" placeholder="Email (your download link goes here)" value={f.email} onChange={set("email")} required data-testid="buy-email" />
        <Field type="tel" placeholder="Phone (optional, for text delivery)" value={f.phone} onChange={set("phone")} data-testid="buy-phone" />
        <div className="grid grid-cols-2 gap-2 pt-1">
          {[["email", "Email"], ["sms", "Text + Email"]].map(([v, l]) => (
            <button type="button" key={v} onClick={() => setF({ ...f, delivery: v })} data-testid={`buy-delivery-${v}`}
                    className={`py-3 rounded-lg border text-xs font-bold transition-colors ${f.delivery === v ? "border-white bg-white text-black" : "border-white/15 text-white/70 hover:border-white/40"}`}>
              {l}
            </button>
          ))}
        </div>
        <SubmitBtn busy={busy} icon={Lock} testid="buy-submit">{busy ? "REDIRECTING…" : `PAY $${release.price.toFixed(2)}`}</SubmitBtn>
        <div className="text-center text-white/35 text-[10px] tracking-[0.2em] font-mono">SECURE CHECKOUT · STRIPE</div>
      </form>
    </Shell>
  );
};

export const RecoverModal = ({ release, onClose, onToken }) => {
  const [email, setEmail] = useState(localStorage.getItem(`release_${release.slug}_email`) || "");
  const [busy, setBusy] = useState(false);

  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND}/api/releases/${release.slug}/recover`, { email });
      onToken(r.data.token);
      startDownload(r.data.token);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recovery failed.");
      setBusy(false);
    }
  };

  return (
    <Shell onClose={onClose} title="Get your download" subtitle="Enter the email you used at checkout">
      <form onSubmit={go} className="space-y-3" data-testid="recover-form">
        <Field type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="recover-email" />
        <SubmitBtn busy={busy} icon={Download} testid="recover-submit">{busy ? "VERIFYING…" : "DOWNLOAD"}</SubmitBtn>
      </form>
    </Shell>
  );
};

export const SuccessModal = ({ release, token, onClose }) => (
  <Shell onClose={onClose} title="You own it." subtitle={`${release.title} · ${release.artist}`}>
    <div className="text-center" data-testid="purchase-success">
      <CheckCircle2 size={44} className="mx-auto mb-4" strokeWidth={1.5} />
      <p className="text-white/60 text-sm mb-6">Your song and cover art are ready. We also emailed you the link (valid 7 days).</p>
      <button onClick={() => startDownload(token)} data-testid="success-download-btn"
              className="w-full px-6 py-4 rounded-full bg-white text-black text-xs font-black tracking-[0.18em] inline-flex items-center justify-center gap-2 transition-transform duration-200 hover:scale-[1.02]">
        <Download size={14} /> DOWNLOAD NOW
      </button>
    </div>
  </Shell>
);
